import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { Pencil, RefreshCcw } from 'lucide-react';
import { useState } from 'react';
import { useScopes } from '~/components/Providers/AuthContext';
import { ProgressBar } from '~/primitives/progressbar';
import { useLocaleDateString } from '~/util/hooks/useLocale';
import { useRelativetime } from '~/util/hooks/useRelativeTime';
import { bountyCompletionsQuery } from '~/util/queries/public/bounties';
import { EditBountySheet } from './-EditBountySheet';
import { useApiMutation } from '~/util/hooks/useApiMutation';
import { syncBountyMutation } from '~/util/mutations/staff/bounties';
import { Button } from '~/primitives/button';
import { useList } from '~/util/hooks/useList';
import { CompletedByUsers } from '~/components/CompletedByUsers';

export const Route = createFileRoute('/games/bounty-board/$bountyid')({
    loaderDeps: ({ search: { list } }) => ({ list }),
    loader: async ({
        params: { bountyid },
        context: { queryClient, classic, platformer },
        deps: { list },
    }) => {
        const bounty =
            list === 'classic'
                ? classic.find((b) => b.id === bountyid)
                : platformer.find((b) => b.id === bountyid);

        if (!bounty) {
            throw redirect({
                to: '/games/bounty-board',
                replace: true,
            });
        }

        const completions = await queryClient.fetchQuery(
            bountyCompletionsQuery({
                list,
                bountyId: bountyid,
            }),
        );
        return { bounty, completions };
    },

    component: RouteComponent,
});

function RouteComponent() {
    const [editOpen, setEditOpen] = useState(false);

    const list = useList();

    const { bounty, completions } = Route.useLoaderData();
    const localeDate = useLocaleDateString();
    const relativeTimeBeforeEnd = useRelativetime(bounty.end_date ?? undefined);

    completions.sort((a, b) => {
        return a.completed_at.localeCompare(b.completed_at);
    });

    const syncMutation = useApiMutation(syncBountyMutation());

    const { hasScope } = useScopes();

    const isInProgress =
        bounty.end_date && new Date() < new Date(bounty.end_date);

    const currentTimeProgress = bounty.end_date
        ? ((new Date().getTime() - new Date(bounty.start_date).getTime()) /
              (new Date(bounty.end_date).getTime() -
                  new Date(bounty.start_date).getTime())) *
          100
        : undefined;

    return (
        <div className="p-4 w-full h-full flex flex-col gap-4">
            <div className="flex justify-between">
                <h2 className="uppercase font-semibold w-fit border-b-2 border-secondary-foreground pb-2">
                    {bounty.bounty_type} Extreme Demon
                </h2>
                {hasScope('bounty_manage') && (
                    <Pencil
                        className="size-8 hover:text-secondary-light transition-colors cursor-pointer"
                        onClick={() => {
                            setEditOpen(true);
                        }}
                    />
                )}
            </div>
            <Link
                to="/list/$levelid"
                params={{ levelid: String(bounty.level.level_id) }}
                className="text-secondary-foreground clamp-[text-xl-2xl-clamp] font-semibold"
            >
                <span className="text-secondary-light">
                    {bounty.level.status === 'MainList'
                        ? `#${bounty.level.position}`
                        : bounty.level.status}{' '}
                </span>
                {bounty.level.name}
            </Link>
            <div className="flex flex-col gap-0.5">
                <div className="flex justify-between font-semibold">
                    <p>{localeDate(bounty.start_date)}</p>
                    {bounty.end_date && <p>{localeDate(bounty.end_date)}</p>}
                </div>
                <ProgressBar
                    value={currentTimeProgress ?? 100}
                    max={100}
                    getValueLabel={() =>
                        isInProgress
                            ? `Ends ${relativeTimeBeforeEnd}`
                            : bounty.end_date
                              ? 'Ended'
                              : 'No end date'
                    }
                    isLabelVisible={true}
                    height={20}
                />
            </div>
            {hasScope('bounty_manage') && (
                <Button
                    className="flex items-center gap-2 w-fit"
                    onClick={() => syncMutation.mutate({ id: bounty.id, list })}
                >
                    <RefreshCcw /> Sync completions
                </Button>
            )}

            {bounty.target_submissions ? (
                <>
                    <p>
                        This bounty will be closed after{' '}
                        {bounty.target_submissions} people have completed it.
                    </p>
                    <ProgressBar
                        value={bounty.current_completions}
                        max={bounty.target_submissions}
                        getValueLabel={(value, max) =>
                            `${value}/${max} completions`
                        }
                        isLabelVisible={true}
                        height={20}
                    />
                </>
            ) : null}

            {completions.length > 0 ? (
                <div className="mt-4 flex min-h-0 flex-1 flex-col">
                    <h3 className="text-lg font-semibold">
                        Completed by: ({completions.length})
                    </h3>
                    <CompletedByUsers
                        completions={completions}
                        showTrophies
                        className="min-h-0 flex-1"
                    />
                </div>
            ) : (
                <p className="text-center text-secondary-light mt-4">
                    No one has completed this bounty.
                </p>
            )}

            {hasScope('bounty_manage') && (
                <EditBountySheet open={editOpen} onOpenChange={setEditOpen} />
            )}
        </div>
    );
}
